import { useState } from 'react';
import { Clock, X } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';

const DAYS = [
  { key: 'mon', label: 'Monday' },
  { key: 'tue', label: 'Tuesday' },
  { key: 'wed', label: 'Wednesday' },
  { key: 'thu', label: 'Thursday' },
  { key: 'fri', label: 'Friday' },
  { key: 'sat', label: 'Saturday' },
  { key: 'sun', label: 'Sunday' }
];

const DEFAULT_HOURS = {
  mon: { open: '09:00', close: '17:00', closed: false },
  tue: { open: '09:00', close: '17:00', closed: false },
  wed: { open: '09:00', close: '17:00', closed: false },
  thu: { open: '09:00', close: '17:00', closed: false },
  fri: { open: '09:00', close: '17:00', closed: false },
  sat: { open: '10:00', close: '16:00', closed: false },
  sun: { open: '10:00', close: '16:00', closed: true }
};

export default function BusinessHoursInput({ value, onChange }) {
  const [hours, setHours] = useState(value || DEFAULT_HOURS);

  const handleDayChange = (day, field, newValue) => {
    const updated = {
      ...hours,
      [day]: {
        ...hours[day],
        [field]: newValue
      }
    };
    setHours(updated);
    onChange(updated);
  };

  const handleClosedToggle = (day, closed) => {
    const updated = {
      ...hours,
      [day]: {
        ...hours[day],
        closed
      }
    };
    setHours(updated);
    onChange(updated);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-zinc-400 mb-4">
        <Clock className="w-4 h-4" />
        <span className="text-sm font-medium">Business Hours</span>
      </div>

      {DAYS.map(({ key, label }) => (
        <div
          key={key}
          className={`flex items-center gap-4 p-3 rounded-lg border transition-colors ${
            hours[key]?.closed
              ? 'bg-zinc-900/30 border-zinc-800/50'
              : 'bg-zinc-900/50 border-zinc-800'
          }`}
        >
          <div className="w-24 shrink-0">
            <span className={`text-sm font-medium ${hours[key]?.closed ? 'text-zinc-600' : 'text-white'}`}>
              {label}
            </span>
          </div>

          {hours[key]?.closed ? (
            <div className="flex-1 text-zinc-500 text-sm">Closed</div>
          ) : (
            <div className="flex items-center gap-2 flex-1">
              <input
                type="time"
                value={hours[key]?.open || '09:00'}
                onChange={(e) => handleDayChange(key, 'open', e.target.value)}
                className="bg-zinc-800 border border-zinc-700 rounded-lg px-2 py-1 text-white text-sm focus:outline-none focus:ring-2 focus:ring-white/20"
                data-testid={`hours-${key}-open`}
              />
              <span className="text-zinc-500">to</span>
              <input
                type="time"
                value={hours[key]?.close || '17:00'}
                onChange={(e) => handleDayChange(key, 'close', e.target.value)}
                className="bg-zinc-800 border border-zinc-700 rounded-lg px-2 py-1 text-white text-sm focus:outline-none focus:ring-2 focus:ring-white/20"
                data-testid={`hours-${key}-close`}
              />
            </div>
          )}

          <div className="flex items-center gap-2">
            <Label className="text-zinc-500 text-xs">Closed</Label>
            <Switch
              checked={hours[key]?.closed || false}
              onCheckedChange={(checked) => handleClosedToggle(key, checked)}
              data-testid={`hours-${key}-closed`}
            />
          </div>
        </div>
      ))}

      <p className="text-zinc-600 text-xs mt-2">
        Set your regular business hours. Mark days as "Closed" if you don't operate.
      </p>
    </div>
  );
}
